import { ExternalLink, Search, X } from 'lucide-react'
import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import Highlighter from 'react-highlight-words'
import {
	RegionContext,
	RegionUnderRow,
} from '#app/components/assistant-ui/region-picker.js'
import { RecentSearchToggle } from '#app/components/recent-search-toggle.js'
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from '#app/components/ui/dialog.js'
import { Logo } from '#app/components/ui/logo-icon.js'
import { type SnabbsokResponse } from '#app/routes/resources+/snabbsok.ts'
import { useChatSessionId } from '#app/utils/chat-session.js'
import {
	SEARCH_MODAL_SURFACE,
	shouldRecordSearch,
	trackSearchResultClick,
	trackSearchSettled,
	trackSearchStarted,
	trackUIEvent,
} from '#app/utils/plausible.client.js'
import {
	readRecentSearches,
	removeRecentSearch,
	saveRecentSearch,
	type RecentSearch,
} from '#app/utils/recent-searches.ts'
import { dialogRows, type DialogRow } from '#app/utils/search-dialog-rows.ts'
import { querify, synonymify } from '#app/utils/search-text.ts'
import {
	fireFeedbackClick,
	snabbsokClickPayload,
	snabbsokSearchRequest,
} from '#app/utils/snabbsok-feedback.client.ts'
import { useRegionParam } from '#app/utils/use-region-param.ts'

/**
 * Navbarens sökdialog. Utseende och beteende följer ⌘K-modalen på
 * internetmedicin.se, som är facit för sökytor - MINUS AI-rutan: här står man
 * redan i assistenten, och chattrutan är vägen till ett AI-svar.
 *
 * Söket går direkt mot assistentens egen motor via /resources/snabbsok, samma
 * route som Snabbsök-kortet i chatten. Visningar och klick loggas därför på
 * samma sätt som kortets (snabbsok_impressions med `location` =
 * 'assistent_modal', klick via /resources/snabbsok-click). En visningsrad per
 * avskickad sökning med träffar, alltså per skrivpaus - som sajtens modal, som
 * loggar varje /search-anrop. Kortet loggar en per fråga, så jämför
 * klickfrekvens per yta, inte rakt över ytorna.
 *
 * Avsiktliga skillnader mot sajtens modal:
 * - En hybridsökning per skrivpaus i stället för sajtens lexikala mellansteg,
 *   se SEARCH_DEBOUNCE_MS.
 * - Varje träff öppnas i NY flik. Chattråden finns bara i minnet, så en
 *   navigering i samma flik kastade den pågående konversationen.
 * - Ingen annons.
 */

/**
 * En sökning per skrivpaus: debouncen nollställs vid varje tangenttryckning,
 * och Enter hoppar över väntan. Motorn kör vektorbenet på varje anrop (som för
 * kortet), så en långsam skrivare - över 450 ms mellan tangenterna - betalar
 * ett vektoranrop per tecken. Sajtens tvåstegskoreografi (lexikalt vid 300 ms,
 * hybrid vid 450) gav inget här: hybridsteget avbröt det lexikala svaret
 * innan det hann målas i de flesta fall (uppmätt).
 */
const SEARCH_DEBOUNCE_MS = 450

// Fördröjningen innan `search`-händelsen fyras för en färdig fråga - samma som
// sajten: lång nog att skrivpauser inte blir egna händelser, kort nog att hinna
// före ett snabbt klick på en träff.
const SEARCH_EVENT_DEBOUNCE_MS = 700

/** Motorns golv: kortare frågor besvaras tomt (samma gräns som spårningen). */
const searchable = (value: string) => shouldRecordSearch(value)

/** Sökningen ett visat svar hör till. */
type ShownSearch = {
	query: string
	region: string
	/** Null när ingen impression loggades (ingen session). */
	impressionId: string | null
	startedAt: number
}

type ShownResult = { results: SnabbsokResponse; search: ShownSearch }

export function SearchDialog({
	isOpen,
	setIsOpen,
}: {
	isOpen: boolean
	setIsOpen: (open: boolean) => void
}) {
	// Rensning = montera om panelen, samma mekanism som stängning. Att bara
	// nolla fältet räcker inte: ett svar för den övergivna frågan kan landa
	// efter att användaren börjat skriva igen. En omontering dödar fält, anrop
	// och det övergivna svaret tillsammans.
	const [resetKey, setResetKey] = useState(0)
	const { region, selectRegion } = useRegionParam('search_modal')

	useEffect(() => {
		if (isOpen) {
			trackUIEvent({ eventName: 'button_click', label: 'open_search_modal' })
		}
	}, [isOpen])

	return (
		<Dialog open={isOpen} onOpenChange={setIsOpen}>
			<DialogContent
				hideCloseButton
				className="top-2 flex max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] translate-y-0 flex-col gap-0 overflow-hidden rounded-xl bg-white p-0 dark:bg-gray-800 sm:top-[8%] sm:max-h-[84dvh] lg:max-w-3xl lg:rounded-3xl"
			>
				<DialogTitle className="sr-only">Sök</DialogTitle>
				<RegionContext.Provider
					value={{ region, onSelectRegion: selectRegion }}
				>
					<SearchPanel
						key={resetKey}
						open={isOpen}
						region={region}
						onClear={() => setResetKey(k => k + 1)}
						onClose={() => setIsOpen(false)}
						onSelect={() => setIsOpen(false)}
					/>
				</RegionContext.Provider>
				{/* Foten bär opt-in för "Tidigare", som på sajten. På mobil får
				    brytaren hela raden; ordmärket är det första som kan strykas. */}
				<div className="flex items-center justify-between border-t border-gray-300 p-3 dark:border-gray-700 lg:px-8 lg:py-3">
					<RecentSearchToggle />
					<span className="hidden items-center gap-2 lg:flex">
						<span className="font-bold text-gray-900 dark:text-gray-200">
							Internetmedicin
						</span>
						<Logo className="h-7 w-7" />
					</span>
				</div>
			</DialogContent>
		</Dialog>
	)
}

/**
 * Panelen monteras INUTI DialogContent, som Radix avmonterar när dialogen
 * stängs. Därför nollställs fältet, anropet och en eventuell schemalagd
 * sökning av sig självt vid varje öppning.
 */
function SearchPanel({
	open,
	region,
	onClear,
	onClose,
	onSelect,
}: {
	open: boolean
	region: string
	/** Montera om panelen — se kommentaren i SearchDialog. */
	onClear: () => void
	onClose: () => void
	onSelect: () => void
}) {
	const sessionId = useChatSessionId()
	const [query, setQuery] = useState('')
	const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])
	const [shown, setShown] = useState<ShownResult | undefined>()
	// Schemalagd ELLER i luften. Under debouncefönstret finns inget anrop än,
	// men panelen får ändå inte påstå något om förra frågans svar.
	const [pending, setPending] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout>>()
	const ctrlRef = useRef<AbortController>()

	// Läses efter montering (localStorage finns inte vid SSR) och därmed vid
	// varje öppning, eftersom panelen monteras om då.
	useEffect(() => {
		setRecentSearches(readRecentSearches())
	}, [])

	// Vanlig fetch, som Snabbsök-kortet, och inte en Remix-fetcher: ett
	// nätverksfel i en fetcher hamnar i rotens felgräns och kastar chattråden.
	// Ett avbrutet anrop (ny fråga, rensning, stängning) släpps tyst; routen
	// fullföljer sitt motoranrop ändå, se /resources/snabbsok.
	const runSearch = (value: string, searchRegion: string) => {
		ctrlRef.current?.abort()
		const ctrl = new AbortController()
		ctrlRef.current = ctrl
		const q = value.trim()
		const { params, impressionId, headers } = snabbsokSearchRequest({
			query: q,
			region: searchRegion,
			sessionId,
			location: SEARCH_MODAL_SURFACE,
		})
		const search: ShownSearch = {
			query: q,
			region: searchRegion,
			impressionId: sessionId ? impressionId : null,
			startedAt: Date.now(),
		}
		const show = (results: SnabbsokResponse) => {
			if (ctrl.signal.aborted) return
			setShown({ results, search })
			setPending(false)
		}
		fetch(`/resources/snabbsok?${params.toString()}`, {
			signal: ctrl.signal,
			headers,
		})
			.then(r =>
				r.ok
					? (r.json() as Promise<SnabbsokResponse>)
					: Promise.reject(new Error(`snabbsok ${r.status}`)),
			)
			.then(show)
			// Nätet eller servern svarade inte: samma besked som när motorn är
			// nere, aldrig "Inga resultat".
			.catch(() => show({ im: [], ext: [], degraded: true }))
	}

	const cancelSearch = () => {
		clearTimeout(timerRef.current)
		ctrlRef.current?.abort()
	}

	// Enter betyder "sök nu", ovillkorligt (även för en redan besvarad fråga).
	const submitSearch = (
		value: string,
		immediate = false,
		searchRegion = region,
	) => {
		clearTimeout(timerRef.current)
		setPending(true)
		if (immediate) runSearch(value, searchRegion)
		else
			timerRef.current = setTimeout(
				() => runSearch(value, searchRegion),
				SEARCH_DEBOUNCE_MS,
			)
	}

	// Stängning: `open` blir false direkt, medan Radix håller panelen monterad
	// genom utgångsanimeringen (~200 ms) - en schemalagd sökning hann annars
	// fyra mot en stängd dialog. Avmontering (rensning, stängning) städar också.
	const cancelRef = useRef(cancelSearch)
	cancelRef.current = cancelSearch
	useEffect(() => {
		if (!open) cancelRef.current()
	}, [open])
	useEffect(() => () => cancelRef.current(), [])

	// `search_started`, en gång per påbörjad sökning. Fyras bara från riktig
	// inmatning (onChange). Återarmeringen sköts av ommonteringen.
	const startedRef = useRef(false)
	const noteSearchStarted = (value: string) => {
		if (startedRef.current || !shouldRecordSearch(value)) return
		startedRef.current = true
		trackSearchStarted({ region })
	}

	// Ett regionbyte gäller hela sidan (URL:en). Står det en fråga i fältet söks
	// den om direkt med den nya regionen.
	const lastRegionRef = useRef(region)
	useEffect(() => {
		if (region === lastRegionRef.current) return
		lastRegionRef.current = region
		if (searchable(query)) submitSearch(query, true, region)
		// Bara regionbytet ska trigga; frågan läses som den står.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [region])

	const displayResult = searchable(query) ? shown : undefined
	// "En sökning är på väg, så påstå inget om resultatet ännu."
	const searching = searchable(query) && pending

	const rows = useMemo(
		() => (displayResult ? dialogRows(displayResult.results) : []),
		[displayResult],
	)

	// `search` - EN händelse per avsiktlig fråga, inte per tangent: nyckeln
	// debouncas, så "dia → diab → diabetes" blir en. Aldrig medan en sökning
	// pågår (ett tomt mellanläge vore en falsk nollträff), och aldrig när
	// sökningen inte kunde köras (tomt där är inget innehållsglapp) - som på
	// sajten. Regionen ingår i nyckeln: ett regionbyte söker om SAMMA fråga.
	const settledKey =
		!searching && displayResult && !displayResult.results.degraded
			? `${displayResult.search.region}|${displayResult.search.query}`
			: ''
	const settledRef = useRef({ displayResult, rows })
	settledRef.current = { displayResult, rows }
	// Senast räknade nyckel: nyckeln går till '' så fort en ny sökning startar,
	// så Enter på en redan räknad fråga gav annars A → '' → A och en andra
	// händelse. Nollställs med panelen.
	const lastTrackedKeyRef = useRef('')
	useEffect(() => {
		if (!settledKey || settledKey === lastTrackedKeyRef.current) return
		const t = setTimeout(() => {
			const { displayResult: settled, rows: settledRows } = settledRef.current
			if (!settled || !shouldRecordSearch(settled.search.query)) return
			lastTrackedKeyRef.current = settledKey
			trackSearchSettled({
				query: settled.search.query,
				records: settledRows,
				region: settled.search.region,
			})
		}, SEARCH_EVENT_DEBOUNCE_MS)
		return () => clearTimeout(t)
		// Bara nyckeln ska starta om klockan.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [settledKey])

	return (
		<>
			{/* Permanent monterad statusregion: en aria-live-region måste finnas i
			    trädet innan texten ändras för att skärmläsare ska annonsera den. */}
			<div role="status" className="sr-only">
				{searching ? 'Söker…' : ''}
			</div>
			<div className="border-b border-gray-300 p-3 dark:border-gray-700 lg:px-8 lg:pb-4 lg:pt-8">
				<div className="relative">
					<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
						<Search
							className="h-5 w-5 text-gray-500 dark:text-gray-400"
							aria-hidden="true"
						/>
					</div>
					<input
						type="search"
						name="q"
						aria-label="Sök"
						// Routen klipper q vid 200 tecken; utan taket här besvaras en
						// klistrad lång fråga på en trunkerad variant, tyst.
						maxLength={200}
						autoComplete="off"
						autoFocus
						value={query}
						onChange={e => {
							const value = e.target.value
							noteSearchStarted(value)
							setQuery(value)
							// Tömt fält (backspace hela vägen) = samma sak som ✕.
							if (!value.trim()) onClear()
							else if (searchable(value)) submitSearch(value)
							// Ett tecken: motorn svarar ändå tomt, så inget anrop och
							// inget "Inga resultat" - bara starttexten.
							else {
								cancelSearch()
								setPending(false)
							}
						}}
						onKeyDown={e => {
							// Enter = klar med att skriva: skickar direkt och rensar den
							// schemalagda timern. e.repeat: en nedhållen Enter hade
							// kostat ett vektoranrop per upprepning.
							if (e.key !== 'Enter' || e.repeat) return
							e.preventDefault()
							const value = e.currentTarget.value
							if (searchable(value)) submitSearch(value, true)
						}}
						className="block w-full appearance-none rounded-lg border border-gray-300 bg-gray-50 p-3 pl-10 pr-28 text-base text-gray-900 focus:outline-none focus:ring-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 lg:p-4 lg:pl-12 lg:pr-32 lg:text-lg [&::-webkit-search-cancel-button]:hidden"
						placeholder="Sök"
					/>
					{query && (
						<button
							type="button"
							aria-label="Rensa sökning"
							onClick={onClear}
							className="absolute right-[4.75rem] top-1/2 -translate-y-1/2 rounded-lg p-2 text-gray-500 hover:text-gray-700 focus:outline-none dark:text-gray-400 dark:hover:text-gray-200"
						>
							<X className="h-5 w-5" aria-hidden="true" />
						</button>
					)}
					{/* Ordet "Avbryt" för stäng och ikonen för rensa, som på sajten
					    (och iOS): två kryss bredvid varandra gick inte att skilja åt. */}
					<button
						type="button"
						onClick={onClose}
						className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg px-3 py-2 text-sm font-medium text-blue-600 focus:outline-none focus:ring-2 dark:text-blue-400"
					>
						Avbryt
					</button>
				</div>
				{/* Samma rad som under chattrutan ("Söker i …"); valet gäller hela
				    sidan, så det följer med till nästa fråga i chatten. */}
				<RegionUnderRow
					selectId="search-dialog-region"
					infoId="search-dialog-region-info"
					testId="search-dialog-region-row"
				/>
			</div>
			<div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-2 lg:px-8 lg:py-4">
				<SearchResults
					result={displayResult}
					rows={rows}
					searching={searching}
					sessionId={sessionId}
					recentSearches={recentSearches}
					setRecentSearches={setRecentSearches}
					onSelect={onSelect}
				/>
			</div>
		</>
	)
}

function SearchResults({
	result,
	rows,
	searching,
	sessionId,
	recentSearches,
	setRecentSearches,
	onSelect,
}: {
	result: ShownResult | undefined
	rows: DialogRow[]
	searching: boolean
	sessionId: string
	recentSearches: RecentSearch[]
	setRecentSearches: (list: RecentSearch[]) => void
	onSelect: () => void
}) {
	if (!result) {
		if (searching) return <Searching />
		if (recentSearches.length > 0) {
			return (
				<RecentSearchList
					recentSearches={recentSearches}
					setRecentSearches={setRecentSearches}
					onSelect={onSelect}
				/>
			)
		}
		return (
			<SmallMessage>
				Sök bland behandlingsöversikter och betrodda externa källor
			</SmallMessage>
		)
	}

	// Sökningen kunde inte köras: säg det i stället för "Inga resultat", så
	// ett driftfel inte läses som att innehållet saknas. Samma text som sajten.
	if (result.results.degraded) {
		if (searching) return <Searching />
		return (
			<SmallMessage>
				Sökningen är tillfälligt otillgänglig. Försök igen om en liten stund.
			</SmallMessage>
		)
	}

	if (rows.length === 0) {
		// Tomt svar medan en ny sökning är på väg är inget "Inga resultat" -
		// felstavningar ger ofta tomt tills hybriden svarat.
		if (searching) return <Searching />
		return (
			<SmallMessage>
				Inga resultat för{' '}
				<strong>
					&quot;
					<em className="text-gray-900 dark:text-gray-100">
						{result.search.query}
					</em>
					&quot;
				</strong>
				.
			</SmallMessage>
		)
	}

	const terms = querify(result.search.query).split(' ').filter(Boolean)

	// Listan målas rakt av även när en ny sökning är på väg, som på sajten.
	return (
		<nav aria-label="Sökresultat">
			<ul className="space-y-1">
				{rows.map((row, i) => (
					<SearchResultItem
						key={row.key}
						row={row}
						terms={terms}
						position={i + 1}
						totalShown={rows.length}
						search={result.search}
						sessionId={sessionId}
						setRecentSearches={setRecentSearches}
						onSelect={onSelect}
					/>
				))}
			</ul>
		</nav>
	)
}

/** Väntetillståndet, ett ställe för båda väntelägena. */
function Searching() {
	return (
		<SmallMessage>
			<span className="inline-flex items-center gap-2">
				<span
					aria-hidden="true"
					className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600 dark:border-t-gray-300"
				/>
				Söker…
			</span>
		</SmallMessage>
	)
}

function SmallMessage({ children }: { children: React.ReactNode }) {
	return (
		<p className="my-6 text-base text-gray-500 dark:text-gray-400 lg:text-lg">
			{children}
		</p>
	)
}

/**
 * Källmärket till höger: grönt "Internetmedicin" på interna rader (även
 * specialitetssidor), källans namn i källans färg på externa - samma
 * search-lib-färger som Snabbsök-kortet.
 */
function SourceChip({ row }: { row: DialogRow }) {
	return (
		<span
			className="max-w-[9rem] shrink-0 self-center truncate rounded px-2 py-1 text-xs font-semibold text-white sm:max-w-[13rem]"
			style={{ backgroundColor: row.chipColor }}
			title={row.chipLabel}
		>
			{row.chipLabel}
		</span>
	)
}

function SearchResultItem({
	row,
	terms,
	position,
	totalShown,
	search,
	sessionId,
	setRecentSearches,
	onSelect,
}: {
	row: DialogRow
	terms: string[]
	position: number
	totalShown: number
	search: ShownSearch
	sessionId: string
	setRecentSearches: (list: RecentSearch[]) => void
	onSelect: () => void
}) {
	const { hit, isExternal } = row
	// Varför en intern träff kom upp när ordet inte syns i titeln (t.ex. en kod).
	const relevantSynonyms = isExternal
		? ''
		: synonymify(hit.synonyms ?? null, terms, row.title, hit.description ?? '')

	// Klickraden i snabbsok_clicks, samma kontrakt som kortets. Positionen är
	// platsen i motorns körfält, som i impressionsradens results_shown.
	const logClick = (url: string, sitelinkIndex: number | null) => {
		if (!sessionId) return
		fireFeedbackClick(
			snabbsokClickPayload({
				impressionId: search.impressionId,
				sessionId,
				clickedPosition: row.lanePosition,
				clickedUrl: url,
				clickedSource: row.source,
				clickedSourceType: isExternal ? 'external' : 'im',
				clickedTopicId: hit.topic_id ?? null,
				isSitelink: sitelinkIndex !== null,
				sitelinkIndex,
				startedAt: search.startedAt,
				region: search.region,
			}),
		)
	}

	const onRowClick = () => {
		logClick(row.href, null)
		trackSearchResultClick({
			source: row.source,
			isExternal,
			position,
			totalShown,
			region: search.region,
		})
		trackUIEvent({
			eventName: 'button_click',
			label: isExternal
				? 'clicked_on_external_search_result'
				: 'clicked_on_search_results',
			location: 'search_result',
		})
		// "Tidigare" gäller som på sajten bara interna träffar.
		if (!isExternal) {
			setRecentSearches(
				saveRecentSearch({
					id: row.key,
					title: row.title,
					canonicalPath: hit.page_url ?? row.href,
					type: hit.type ?? '',
					url: row.href,
				}),
			)
		}
		onSelect()
	}

	const onSitelinkClick = (url: string, index: number) => {
		logClick(url, index + 1)
		trackUIEvent({
			eventName: 'button_click',
			label: isExternal
				? 'clicked_on_external_sitelink'
				: 'clicked_on_search_sitelink',
			location: 'search_result',
		})
		onSelect()
	}

	return (
		<li>
			<div
				className={
					isExternal
						? '-mx-1 rounded-lg bg-amber-50 dark:bg-amber-950/25 lg:focus-within:bg-amber-100/70 lg:hover:bg-amber-100/70 lg:hover:dark:bg-amber-950/40'
						: '-mx-1 rounded-lg bg-gray-50 dark:bg-gray-900 lg:focus-within:bg-gray-100 lg:hover:bg-gray-100 lg:focus-within:dark:bg-gray-700 lg:hover:dark:bg-gray-700'
				}
			>
				{/* Ny flik för alla träffar: chattråden finns bara i minnet och
				    hade gått förlorad vid en navigering i samma flik. */}
				<a
					href={row.href}
					target="_blank"
					rel="noopener noreferrer"
					onClick={onRowClick}
					className="block rounded-lg px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-50"
				>
					<div className="flex items-center justify-between gap-2">
						<div className="min-w-0">
							<span className="flex min-w-0 items-center gap-1">
								<span className="block min-w-0 truncate text-[15px] font-semibold leading-tight text-gray-900 dark:text-gray-100 lg:text-base">
									<Highlighter
										searchWords={terms}
										autoEscape
										highlightTag="span"
										highlightClassName="underline decoration-pink-500/50 decoration-2 underline-offset-2"
										textToHighlight={row.title}
									/>
									{relevantSynonyms && (
										<>
											{' '}
											<span className="underline decoration-pink-500/50 decoration-2 underline-offset-2">
												({relevantSynonyms})
											</span>
										</>
									)}
								</span>
								{isExternal && (
									<>
										<ExternalLink
											className="h-3.5 w-3.5 shrink-0 text-gray-500 dark:text-gray-400"
											aria-hidden="true"
										/>
										<span className="sr-only">
											Extern länk, öppnas i ny flik
										</span>
									</>
								)}
							</span>
							{/* Innehållstypen som dämpad text, aldrig som ett annat chip:
							    chipet är ett källmärke. */}
							{row.isSpeciality && (
								<span className="mt-0.5 block text-[11px] font-medium text-gray-500 dark:text-gray-400">
									Specialitetssida
								</span>
							)}
						</div>
						<SourceChip row={row} />
					</div>
				</a>
				{row.sitelinks.length > 0 && (
					<div className="-mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 px-3 pb-1.5">
						{row.sitelinks.map((sitelink, sitelinkI) => (
							<Fragment key={sitelink.url}>
								{sitelinkI > 0 && (
									<span
										className="select-none text-gray-400 dark:text-gray-600"
										aria-hidden="true"
									>
										·
									</span>
								)}
								<a
									href={sitelink.url}
									target="_blank"
									rel="noopener noreferrer"
									onClick={() => onSitelinkClick(sitelink.url, sitelinkI)}
									className={
										isExternal
											? 'rounded text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-300 dark:hover:text-white'
											: 'rounded text-xs font-medium text-green-600 hover:text-green-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-green-400 dark:hover:text-green-300'
									}
								>
									{sitelink.title}
								</a>
							</Fragment>
						))}
					</div>
				)}
			</div>
		</li>
	)
}

/** "Tidigare" - de senast besökta träffarna, när användaren slagit på det. */
function RecentSearchList({
	recentSearches,
	setRecentSearches,
	onSelect,
}: {
	recentSearches: RecentSearch[]
	setRecentSearches: (list: RecentSearch[]) => void
	onSelect: () => void
}) {
	return (
		<div>
			<h2 className="my-2 font-bold text-gray-700 dark:text-gray-400">
				Tidigare
			</h2>
			<nav aria-label="Tidigare sökningar">
				<ul className="space-y-1">
					{recentSearches.map(({ item }) => (
						<li
							key={item.id}
							className="-mx-1 flex items-center justify-between gap-2 rounded-lg bg-gray-50 dark:bg-gray-900 lg:hover:bg-gray-100 lg:hover:dark:bg-gray-700"
						>
							<a
								href={item.url}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() => {
									trackUIEvent({
										eventName: 'button_click',
										label: 'clicked_on_recent_search',
										location: 'search_result',
									})
									onSelect()
								}}
								className="min-w-0 flex-1 truncate rounded-lg px-3 py-1.5 text-[15px] font-semibold leading-tight text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-gray-100 lg:text-base"
							>
								{item.title}
							</a>
							{/* Syskon, inte barn till länken: en knapp inuti en länk
							    är ogiltig HTML. */}
							<button
								type="button"
								aria-label={`Ta bort ${item.title}`}
								onClick={() => setRecentSearches(removeRecentSearch(item.id))}
								className="mr-1 shrink-0 rounded-lg p-1.5 text-gray-400 transition focus:outline-none focus-visible:ring focus-visible:ring-blue-500 lg:hover:bg-gray-200 lg:hover:text-gray-700 dark:lg:hover:bg-black"
							>
								<X className="h-5 w-5" aria-hidden="true" />
							</button>
						</li>
					))}
				</ul>
			</nav>
		</div>
	)
}
